import type { WizardProps as StepperProps } from 'react-use-wizard';

import { useWizard as useStepper, Wizard as Stepper } from 'react-use-wizard';
import { createContextFactory } from '@mfw/components';
import React from 'react';

type WizardType<TSteps, TWizardState extends Record<string, any> = any> =
  TSteps extends Readonly<Array<string>>
    ? {
        setWizardState: React.Dispatch<React.SetStateAction<TWizardState>>;
        onStepChange?: (step: TSteps[number]) => void;
        goToStep?: (s: TSteps[number]) => void;
        wizardState: TWizardState;
        steps: TSteps;
      }
    : never;

const WizardContext = createContextFactory({
  devToolsName: 'WizardContext',
  initialValue: {} as WizardType<Readonly<Array<string>>>,
});

export interface WizardProps<
  TSteps extends Readonly<Array<string>>,
> extends Omit<StepperProps, 'onStepChange'> {
  onStepChange?: (step: TSteps[number]) => void;

  /**
   * Steps to be displayed.
   * TODO Create step type instead of JSX.Element
   */
  children: JSX.Element | JSX.Element[];
  /**
   * Ordered list of steps to be used. Order matters because name of section determines goToStep method in context
   */
  steps: TSteps;
}
const Wizard = <
  TSteps extends Readonly<Array<string>>,
  TWizardState extends Record<string, any> = any,
>({
  children,
  footer,
  header,
  onStepChange,
  startIndex = 0,
  steps,
}: WizardProps<TSteps>) => {
  const [wizardState, setWizardState] = React.useState<TWizardState>();
  const handleStepChange = React.useCallback(
    (step: number) => {
      const found = steps?.[step];
      // Developer issue, should never happen to actual user
      if (!found) throw new Error('Wizard step does not exist');
      onStepChange?.(found);
    },
    [onStepChange, steps],
  );

  const memoizedValue = React.useMemo(
    () => ({ setWizardState, steps, wizardState }),
    [wizardState, setWizardState, steps],
  );

  return (
    <WizardContext.Provider value={memoizedValue}>
      <Stepper
        onStepChange={handleStepChange}
        startIndex={startIndex}
        header={header}
        footer={footer}
      >
        {children}
      </Stepper>
    </WizardContext.Provider>
  );
};

export function useWizard<
  TSteps extends Readonly<Array<string>>,
  TWizardState extends Record<string, any> = any,
>() {
  const stepper = useStepper();
  const wizard = WizardContext.useContext();

  const goToStep = React.useCallback(
    (step: TSteps[number], state?: TWizardState) => {
      const index = wizard.steps.findIndex((s) => s === step);
      // Developer issue, should never happen to actual user
      if (index === -1) throw new Error('Wizard step does not exist');
      wizard.setWizardState(state);
      stepper.goToStep(index);
    },
    [wizard, stepper],
  );

  const memoizedValue = React.useMemo(() => {
    return {
      ...stepper,
      ...wizard,
      goToStep,
      numberOfSteps: wizard.steps.length,
    };
  }, [stepper, wizard, goToStep]);

  return memoizedValue;
}

export default Wizard;
